import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useSEO } from "../lib/seo";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Markdown } from "../components/Markdown";
import { Scale, ChevronRight, FileText, ArrowLeft, Cookie, ShieldCheck, Euro, Building2, Settings2 } from "lucide-react";
import { Button } from "../components/ui/button";

const ICONS = {
  "termos-e-condicoes": FileText,
  "politica-de-privacidade": ShieldCheck,
  "politica-de-cookies": Cookie,
  "politica-de-reembolso": Euro,
  "aviso-legal": Building2,
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("pt-PT", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return "";
  }
};

export default function Legal() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get("/legal").then((r) => setPages(r.data.pages || [])).catch(() => setPages([]));
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    setNotFound(false);
    if (!slug) {
      setPage(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    api
      .get(`/legal/${slug}`)
      .then((r) => setPage(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const seo = useMemo(
    () => ({
      title: page
        ? `${page.title} | FIRST2JOB`
        : "Informação legal — Termos, Privacidade, Cookies e Reembolsos | FIRST2JOB",
      description: page?.summary || "Termos e Condições, Política de Privacidade (RGPD), Política de Cookies, Política de Reembolso e Aviso Legal da plataforma FIRST2JOB.",
    }),
    [page]
  );
  useSEO(seo);

  const Menu = (
    <nav className="flex flex-col gap-1" aria-label="Documentos legais">
      {pages.map((p) => {
        const Icon = ICONS[p.slug] || FileText;
        const active = p.slug === slug;
        return (
          <Link
            key={p.slug}
            to={`/legal/${p.slug}`}
            data-testid={`legal-menu-${p.slug}`}
            className={`group flex items-start gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors ${
              active
                ? "bg-slate-900 text-white shadow-sm"
                : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
            }`}
          >
            <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${active ? "text-amber-400" : "text-slate-400 group-hover:text-amber-500"}`} />
            <span className="font-medium leading-snug">{p.title}</span>
          </Link>
        );
      })}
      <button
        type="button"
        data-testid="legal-menu-cookie-prefs"
        onClick={() => window.dispatchEvent(new Event("f2j:open-cookie-prefs"))}
        className="mt-2 flex items-start gap-3 rounded-xl px-3 py-2.5 text-sm text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors text-left"
      >
        <Settings2 className="w-4 h-4 mt-0.5 shrink-0 text-slate-400" />
        <span className="font-medium leading-snug">Preferências de cookies</span>
      </button>
    </nav>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Navbar />

      <div className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12 sm:py-16">
          <div className="flex items-center gap-2 text-xs text-slate-400 mb-4">
            <Link to="/" className="hover:text-amber-300 transition-colors">Início</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/legal" className="hover:text-amber-300 transition-colors">Legal</Link>
            {page && (
              <>
                <ChevronRight className="w-3 h-3" />
                <span className="text-slate-300">{page.title}</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span className="w-11 h-11 rounded-2xl bg-amber-400 text-slate-900 flex items-center justify-center shrink-0">
              <Scale className="w-5 h-5" />
            </span>
            <div>
              <h1 className="text-2xl sm:text-4xl font-bold tracking-tight" data-testid="legal-title">
                {page ? page.title : "Informação legal"}
              </h1>
              <p className="text-sm text-slate-400 mt-1">
                {page
                  ? `Última atualização: ${fmtDate(page.updated_at)} · versão ${page.version}`
                  : "Transparência total: todas as políticas da plataforma num só lugar."}
              </p>
            </div>
          </div>
        </div>
      </div>

      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-8 py-10 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8">
        <aside className="lg:sticky lg:top-24 h-fit bg-white rounded-2xl border border-slate-200 p-4">
          <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-400 px-3 mb-3">Documentos</p>
          {Menu}
        </aside>

        <section>
          {!slug && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="legal-index">
              {pages.map((p) => {
                const Icon = ICONS[p.slug] || FileText;
                return (
                  <Link
                    key={p.slug}
                    to={`/legal/${p.slug}`}
                    className="group bg-white rounded-2xl border border-slate-200 p-6 hover:border-amber-400 hover:shadow-lg transition-all"
                  >
                    <span className="w-10 h-10 rounded-xl bg-slate-900 text-amber-400 flex items-center justify-center mb-4">
                      <Icon className="w-5 h-5" />
                    </span>
                    <h2 className="font-bold text-slate-900 group-hover:text-slate-900">{p.title}</h2>
                    <p className="text-sm text-slate-500 mt-2 leading-relaxed">{p.summary}</p>
                    <span className="inline-flex items-center gap-1 text-sm font-semibold text-slate-900 mt-4">
                      Ler <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                    </span>
                  </Link>
                );
              })}
            </div>
          )}

          {slug && loading && (
            <div className="bg-white rounded-2xl border border-slate-200 p-8 space-y-4 animate-pulse">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="h-4 bg-slate-100 rounded" style={{ width: `${70 + (i % 3) * 10}%` }} />
              ))}
            </div>
          )}

          {slug && !loading && notFound && (
            <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
              <h2 className="text-lg font-bold text-slate-900">Documento não encontrado</h2>
              <p className="text-sm text-slate-500 mt-2">Verifica o endereço ou escolhe um documento na lista.</p>
              <Button className="mt-6" onClick={() => navigate("/legal")}>
                <ArrowLeft className="w-4 h-4 mr-1.5" /> Ver todos os documentos
              </Button>
            </div>
          )}

          {slug && !loading && page && (
            <article className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-10" data-testid="legal-content">
              {page.summary && (
                <p className="text-slate-500 text-sm border-b border-slate-100 pb-6 mb-8">{page.summary}</p>
              )}
              <Markdown content={page.content} />
              <div className="mt-10 pt-6 border-t border-slate-100 flex flex-wrap gap-3">
                {pages
                  .filter((p) => p.slug !== slug)
                  .slice(0, 3)
                  .map((p) => (
                    <Link
                      key={p.slug}
                      to={`/legal/${p.slug}`}
                      className="text-xs font-semibold border border-slate-200 rounded-full px-3 py-1.5 text-slate-600 hover:border-amber-400 hover:text-slate-900 transition-colors"
                    >
                      {p.title}
                    </Link>
                  ))}
              </div>
            </article>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
}
